
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import ImageUploading from "react-images-uploading";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { Camera, Pencil, Trash2 } from "lucide-react";
import { useModalContext } from "./ModalProvider";
import { userAPI } from "../UserAPI";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/** Matches tri-model `Photo`: string URL or `{ url, purpose }`. */
export type EntityPhoto = string | { url: string; purpose: string };

function photoUrl(p: EntityPhoto): string {
  return typeof p === "string" ? p : p.url;
}

function isObjectPhoto(p: EntityPhoto): p is { url: string; purpose: string } {
  return typeof p === "object" && p !== null && "url" in p;
}

/** Matches label row (purpose + edit) so add-placeholder aligns with image tops. */
const PHOTO_LABEL_SLOT_MIN_PX = 56;

export interface EntityPhotoListEditorProps {
  /** Current list (undefined / null treated as empty). */
  photos: EntityPhoto[] | undefined | null;
  /** Persist the new list (e.g. call updateEntity / updateSpace). */
  onPhotosChange: (next: EntityPhoto[]) => void | Promise<void>;
  /** Disable add/remove/edit. */
  disabled?: boolean;
}

export default function EntityPhotoListEditor({
  photos,
  onPhotosChange,
  disabled,
}: EntityPhotoListEditorProps) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const list = Array.isArray(photos) ? photos : [];
  const [pendingImages, setPendingImages] = useState<any[]>([]);
  const [purposeDialog, setPurposeDialog] = useState<{
    index: number;
    draft: string;
  } | null>(null);
  const purposeFieldId = useId();

  const displaySrc = useCallback((p: EntityPhoto) => {
    const raw = photoUrl(p);
    return userAPI.getResourceURL(raw);
  }, []);

  const apply = async (next: EntityPhoto[]) => {
    await Promise.resolve(onPhotosChange(next));
  };

  const removeAt = (index: number) => {
    if (disabled) return;
    void apply(list.filter((_, i) => i !== index));
  };

  const openPurposeDialog = (index: number) => {
    const p = list[index];
    const draft = isObjectPhoto(p) ? p.purpose : "";
    setPurposeDialog({ index, draft });
  };

  const savePurposeDialog = async () => {
    if (purposeDialog == null || disabled) return;
    const { index, draft } = purposeDialog;
    const next = [...list];
    const cur = next[index];
    const url = typeof cur === "string" ? cur : cur.url;
    next[index] = { url, purpose: draft };
    setPurposeDialog(null);
    await apply(next);
  };

  const onPendingUploadChange = async (imageList: any[]) => {
    if (!imageList?.length || disabled) return;
    try {
      const formData = new FormData();
      formData.append("file", imageList[0].file);
      const result = await userAPI.uploadFile(formData);
      const path =
        (result && (result.path || result.url || result.fileUrl)) as
          | string
          | undefined;
      if (!path) {
        throw new Error(
          t("entityPhotoList.uploadNoPath") || "Upload did not return a file path",
        );
      }
      await apply([...list, path]);
      setPendingImages([]);
    } catch (e) {
      pushError(e);
      setPendingImages([]);
    }
  };

  return (
    <div className="min-w-0">
      <h3 className="mb-4 text-sm font-bold leading-tight text-[color:var(--customer-primary-dark,hsl(var(--foreground)))]">
        {t("entityPhotoList.title")}
      </h3>

      <ImageUploading
        value={pendingImages}
        onChange={onPendingUploadChange}
        maxNumber={1}
        maxFileSize={8_000_000}
      >
        {({
          onImageUpload,
          dragProps,
          isDragging,
        }) => (
          <div className="flex min-w-0 flex-wrap items-start gap-4">
            <div className="flex w-[200px] max-w-full flex-col items-center">
              <div
                className="mb-2 w-full"
                style={{ minHeight: PHOTO_LABEL_SLOT_MIN_PX }}
                aria-hidden="true"
              />
              {/* Ablage- und Auswahlfläche; beim Ziehen bekommt sie den
                  Warnrahmen aus den Tokens statt der früheren roten Farbe. */}
              <button
                type="button"
                className={cn(
                  "flex h-[300px] w-full flex-col items-center justify-center gap-4 rounded-md border-2 border-dashed p-3 text-center",
                  isDragging ? "border-destructive" : "border-input",
                  disabled ? "cursor-default" : "cursor-pointer",
                )}
                aria-label={t("entityPhotoList.dropOrSelect")}
                aria-disabled={disabled || undefined}
                {...dragProps}
                onClick={disabled ? undefined : onImageUpload}
              >
                <Camera aria-hidden="true" className="h-11 w-11 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t("entityPhotoList.dropOrSelect")}
                </span>
              </button>
            </div>

            {list.map((photo, index) => (
              <div
                key={`${photoUrl(photo)}-${index}`}
                className="flex w-[200px] max-w-full flex-col items-center justify-center"
              >
                {isObjectPhoto(photo) ? (
                  <div
                    className="mb-2 flex w-full items-start gap-1"
                    style={{ minHeight: PHOTO_LABEL_SLOT_MIN_PX }}
                  >
                    <span className="min-w-0 flex-1 break-words pr-1 text-sm leading-snug">
                      {photo.purpose || "—"}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      title={t("entityPhotoList.editPurpose")}
                      aria-label={t("entityPhotoList.editPurpose")}
                      disabled={disabled}
                      onClick={() => openPurposeDialog(index)}
                    >
                      <Pencil aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div
                    className="mb-2 w-full"
                    style={{ minHeight: PHOTO_LABEL_SLOT_MIN_PX }}
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="gap-2"
                      disabled={disabled}
                      onClick={() => openPurposeDialog(index)}
                    >
                      <Pencil aria-hidden="true" className="h-4 w-4" />
                      {t("entityPhotoList.editPurpose")}
                    </Button>
                  </div>
                )}
                <Zoom>
                  {/* Der Verwendungszweck ist die Bildbeschreibung, sofern
                      einer erfasst ist — sonst bleibt es beim Sammelbegriff. */}
                  <div
                    role="img"
                    aria-label={
                      isObjectPhoto(photo) && photo.purpose
                        ? photo.purpose
                        : t("entityPhotoList.title")
                    }
                    className="h-[300px] w-[200px] max-w-full rounded-md border bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: `url(${displaySrc(photo)})` }}
                  />
                </Zoom>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1 gap-2"
                  disabled={disabled}
                  onClick={() => removeAt(index)}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                  {t("global.delete")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </ImageUploading>

      <Dialog
        open={purposeDialog != null}
        onOpenChange={(next) => {
          if (!next) setPurposeDialog(null);
        }}
      >
        {/* `isBlocking` des Bestands: ein Klick daneben schließt nicht. */}
        <DialogContent
          className="max-w-none sm:w-[400px] w-[calc(100vw-2rem)]"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{t("entityPhotoList.editPurpose")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={purposeFieldId}>{t("entityPhotoList.purpose")}</Label>
            <Textarea
              id={purposeFieldId}
              rows={3}
              className="resize-none"
              value={purposeDialog?.draft ?? ""}
              onChange={(e) =>
                setPurposeDialog((d) =>
                  d ? { ...d, draft: e.target.value } : d,
                )
              }
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPurposeDialog(null)}
            >
              {t("global.cancel")}
            </Button>
            <Button type="button" onClick={() => void savePurposeDialog()}>
              {t("global.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
